/**
 * 作品库导入弹窗（参考 EcommerceReplicate 视觉语言）
 *
 * 用途：从用户已有的非导演图片中挑一张，复制为：
 *   - 角色定妆照（target='character', characterId）
 *   - 场景设定图（target='scene', sceneId）
 *   - 分镜 keyframe 候选（target='shot', shotId）
 *
 * 设计：
 *   - 顶部按 model / size 筛选
 *   - 网格展示用户作品（分页加载）
 *   - 单选 + 确认，避免误操作
 *   - 后端走"导入即复制"，存到导演资产路径
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, X, Check, Image as ImageIcon, Filter } from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError } from '../../services/directorApi';
import { api as imageApi, type ModelConfig } from '../../../../services/api';
import { withAuthToken } from '../../../../utils/storageUrl';

export type LibraryImportTarget =
  | { type: 'character'; characterId: string; targetLabel?: string }
  | { type: 'scene'; sceneId: string; targetLabel?: string }
  | { type: 'shot'; shotId: string; targetLabel?: string }
  | { type: 'prop'; propId: string; targetLabel?: string }
  | { type: 'trinitySlot'; shotId: string; slot: 'role' | 'story' | 'video'; targetLabel?: string };

interface Props {
  open: boolean;
  target: LibraryImportTarget;
  onClose: () => void;
  onImported: () => void;
}

interface LibraryImage {
  id: string;
  prompt: string;
  model: string;
  size: string | null;
  resultUrl: string | null;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  createdAt: string;
}

export function ImportFromLibraryDialog({ open, target, onClose, onImported }: Props) {
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 筛选
  const [modelFilter, setModelFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [models, setModels] = useState<ModelConfig[]>([]);

  const pageSize = 24;

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setPage(1);
    imageApi
      .getModels()
      .then((d) => setModels(d.data))
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    directorApi
      .listLibraryImages({
        page,
        pageSize,
        model: modelFilter || undefined,
        size: sizeFilter || undefined,
      })
      .then((result) => {
        setImages(result.data);
        setTotal(result.pagination.total);
      })
      .catch((err) => {
        toast.error(err instanceof DirectorApiError ? err.message : '加载作品库失败');
      })
      .finally(() => setLoading(false));
  }, [open, page, modelFilter, sizeFilter]);

  // 用作筛选的 size 候选（从当前列表 + 常见尺寸归并）
  const sizeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const img of images) {
      if (img.size) set.add(img.size);
    }
    // 常见尺寸兜底
    ['1024x1024', '1024x1536', '1536x1024', '1024x1792', '1792x1024'].forEach((s) => set.add(s));
    return Array.from(set).sort();
  }, [images]);

  if (!open) return null;

  const targetTitle =
    target.type === 'character'
      ? `导入为角色定妆照${target.targetLabel ? ` - ${target.targetLabel}` : ''}`
      : target.type === 'scene'
        ? `导入为场景设定图${target.targetLabel ? ` - ${target.targetLabel}` : ''}`
        : target.type === 'prop'
          ? `导入为物品主图${target.targetLabel ? ` - ${target.targetLabel}` : ''}`
          : target.type === 'trinitySlot'
            ? `导入为三合一卡候选图${target.targetLabel ? ` - ${target.targetLabel}` : ''}`
            : `导入为分镜关键帧候选${target.targetLabel ? ` - ${target.targetLabel}` : ''}`;

  const handleConfirm = async () => {
    if (!selectedId) {
      toast.error('请先选择一张图片');
      return;
    }
    setSubmitting(true);
    try {
      if (target.type === 'character') {
        await directorApi.importToCharacter(target.characterId, selectedId);
      } else if (target.type === 'scene') {
        await directorApi.importToScene(target.sceneId, selectedId);
      } else if (target.type === 'prop') {
        await directorApi.importToProp(target.propId, selectedId);
      } else if (target.type === 'trinitySlot') {
        await directorApi.importToTrinityCardCandidate(target.shotId, target.slot, selectedId);
      } else {
        await directorApi.importToShotKeyframe(target.shotId, selectedId);
      }
      toast.success('已导入');
      onImported();
      onClose();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '导入失败');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 头部 */}
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{targetTitle}</h2>
            <p className="mt-1 text-xs text-slate-500">
              选择已有的作品（导入会复制一份到导演资产，不影响原作品；不消耗积分）
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100"
            aria-label="关闭"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 筛选栏 */}
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-6 py-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
            <Filter className="h-3.5 w-3.5" />
            筛选
          </span>
          <select
            value={modelFilter}
            onChange={(e) => {
              setModelFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-sky-300"
          >
            <option value="">所有模型</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            value={sizeFilter}
            onChange={(e) => {
              setSizeFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-sky-300"
          >
            <option value="">所有尺寸</option>
            {sizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {(modelFilter || sizeFilter) && (
            <button
              type="button"
              onClick={() => {
                setModelFilter('');
                setSizeFilter('');
                setPage(1);
              }}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              重置
            </button>
          )}
        </div>

        {/* 网格 */}
        <div className="flex-1 overflow-y-auto bg-slate-50 px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
            </div>
          ) : images.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <ImageIcon className="mx-auto mb-2 h-10 w-10 opacity-50" />
              <p className="text-sm">作品库还没有可导入的图片</p>
              <p className="mt-1 text-xs">先到主页生成几张图片</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {images.map((img) => {
                const selected = selectedId === img.id;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedId(img.id)}
                    className={`group relative aspect-square overflow-hidden rounded-2xl bg-white transition ${
                      selected
                        ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-slate-50'
                        : 'ring-1 ring-slate-200 hover:ring-sky-300'
                    }`}
                    title={img.prompt}
                  >
                    {img.thumbnailUrl || img.resultUrl ? (
                      <img
                        src={withAuthToken(img.thumbnailUrl || img.resultUrl || '')}
                        alt=""
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-slate-100">
                        <ImageIcon className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    {selected && (
                      <div className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-sky-500 shadow-md">
                        <Check className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                    {img.size && (
                      <div className="absolute bottom-1.5 left-1.5 rounded-full bg-slate-950/80 px-2 py-0.5 text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100">
                        {img.size}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 分页 + 操作 */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-6 py-3">
          <div className="text-xs text-slate-500">
            共 {total} 张{totalPages > 1 && <span className="ml-3">第 {page} / {totalPages} 页</span>}
          </div>
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <>
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 disabled:opacity-40"
                >
                  上一页
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-200 disabled:opacity-40"
                >
                  下一页
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedId || submitting}
              className="inline-flex items-center gap-1.5 rounded-2xl bg-slate-950 px-4 py-1.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              确认导入
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
